import { assign, enqueueActions, fromCallback, setup, type SnapshotFrom } from 'xstate'
import { api, AuthError, type ChatStreamEvent } from '../api'
import { RealtimeASR } from '../asr'
import { saveHistoryCache } from '../historyCache'
import type { StreamingTTS } from '../tts'
import { audioLevel } from './audioLevel'
import { audioStore } from './audioStore'

export interface Message {
    id: number
    role: 'user' | 'assistant'
    content: string
}

export type Mode = 'text' | 'voice'
export type Status = 'idle' | 'listening' | 'thinking' | 'speaking'

interface Ctx {
    messages: Message[]
    liveTranscript: string
    muted: boolean
    voiceActive: boolean // 处于语音模式(asr actor 在跑) —— 给跨区域 sendTo 当守卫
    asrReady: boolean // asr 已 start(麦克风就绪)
    capturing: boolean // ASR 会话真正在录(VAD 触发或手动按住),给 StatusLine 音浪用
    error: string | null
    persona: string
    tts: StreamingTTS | null // 浏览器资源(AudioContext,随 persona 由 hook 维护),经 SET_TTS 注入
    pendingText: string // 本轮用户文本,传给 turnRunner
    onLogout: () => void
}

type ChatEvent =
    | { type: 'ENTER_TEXT' }
    | { type: 'ENTER_VOICE' }
    | { type: 'SEND_TEXT'; text: string }
    | { type: 'INTERRUPT' }
    | { type: 'MUTE' }
    | { type: 'UNMUTE' }
    | { type: 'SET_PERSONA'; persona: string }
    | { type: 'SET_TTS'; tts: StreamingTTS | null }
    | { type: 'INIT_MESSAGES'; messages: Message[] }
    | { type: 'PREPEND_MESSAGES'; messages: Message[] } // 上滑翻页:把更早的历史前插
    | { type: 'SET_TURN_IDS'; id: number } // done 回传真实会话 id:把本轮 user+AI 的假 id 换成真 id
    // 按住说话(手动)
    | { type: 'MANUAL_START' }
    | { type: 'MANUAL_STOP' }
    | { type: 'MANUAL_CANCEL' }
    // 来自 asr actor
    | { type: 'ASR_STARTED' }
    | { type: 'ASR_PARTIAL'; text: string }
    | { type: 'ASR_FINAL'; text: string }
    | { type: 'ASR_ERROR'; error: string }
    | { type: 'ASR_CAPTURE_ON' }
    | { type: 'ASR_CAPTURE_OFF' }
    // 来自 turn actor
    | { type: 'FIRST_TOKEN' }
    | { type: 'REPLY_DELTA'; content: string }
    | { type: 'TURN_DONE' }
    | { type: 'TURN_ERROR'; error: string }

// ASR actor:进语音模式时 invoke,拥有 getUserMedia/VAD/WS。PAUSE/RESUME 经 sendTo 进来,结果经 sendBack 出去。
const asrSession = fromCallback<
    { type: 'PAUSE' | 'RESUME' | 'PUSH_START' | 'PUSH_STOP' | 'PUSH_CANCEL' },
    { engine: string }
>(({ sendBack, receive, input }) => {
    const asr = new RealtimeASR({ engine: input.engine })
    asr.on('partial', (t) => sendBack({ type: 'ASR_PARTIAL', text: String(t ?? '') }))
        .on('final', (t) => sendBack({ type: 'ASR_FINAL', text: String(t ?? '') }))
        .on('error', (e) => sendBack({ type: 'ASR_ERROR', error: String(e) }))
        .on('capture', (active) => sendBack({ type: active ? 'ASR_CAPTURE_ON' : 'ASR_CAPTURE_OFF' }))
        .on('level', (v) => audioLevel.push(Number(v))) // 高频电平走外部 store,不进机器(免整页重渲)
    void asr
        .start()
        .then(() => sendBack({ type: 'ASR_STARTED' }))
        .catch(() => sendBack({ type: 'ASR_ERROR', error: 'asr start failed' }))
    receive((ev) => {
        if (ev.type === 'PAUSE') asr.pause()
        else if (ev.type === 'RESUME') asr.resume()
        else if (ev.type === 'PUSH_START') asr.manualStart()
        else if (ev.type === 'PUSH_STOP') asr.manualStop()
        else if (ev.type === 'PUSH_CANCEL') asr.manualCancel()
    })
    return () => asr.abort()
})

// 一轮对话 actor:chatStream 流式 + 逐句喂 TTS,内部 await TTS 播完(忠实保留原 runTurn 的顺序语义)。
// 被打断/切走时,active 状态退出 → 此 actor 停止 → cleanup 取消 stream + abort TTS。
interface TurnInput {
    text: string
    persona: string
    tts: StreamingTTS | null
    onLogout: () => void
}
const turnRunner = fromCallback<{ type: never }, TurnInput>(({ sendBack, input }) => {
    const { text, persona, tts, onLogout } = input
    let stopped = false
    let fullReply = ''
    let firstToken = true
    let convId: number | undefined // 服务端 done 回传的真实会话 id;音频按它落库
    const task = api.chatStream({ message: text, persona, mode: 'chat' }, (event: ChatStreamEvent) => {
        if (stopped) return
        if (event.error) {
            sendBack({ type: 'TURN_ERROR', error: String(event.error) })
            return
        }
        if (event.content) {
            if (firstToken) {
                firstToken = false
                sendBack({ type: 'FIRST_TOKEN' })
            }
            fullReply += event.content
            sendBack({ type: 'REPLY_DELTA', content: fullReply })
        }
        if (event.sentence) tts?.enqueue(event.sentence, event.index ?? 0)
        // done 带真实会话 id → 把本轮 user+AI 的本地假 id 换成真 id(源头消灭假 id,音频也按真 id 存)
        if (event.conversationId != null) {
            convId = event.conversationId
            sendBack({ type: 'SET_TURN_IDS', id: event.conversationId })
        }
    })
    void (async () => {
        try {
            await task
        } catch (err) {
            if (err instanceof AuthError) {
                onLogout()
                return
            }
            if (!task.isAborted()) sendBack({ type: 'TURN_ERROR', error: String(err) })
        }
        // 等 TTS 播完才算回合结束;打断时 cleanup 已 abort,untilIdle 立刻 resolve
        await tts?.untilIdle()
        if (!stopped) {
            // 回合正常结束:把本轮已播音频按真实会话 id 落库,供气泡显示播放按钮重听。
            // 必须在 sendBack(TURN_DONE) 之前取——TURN_DONE 会让 active 退出、cleanup 调 tts.abort() 清空 captured。
            // 没拿到 convId(罕见:done 没带 id)就不存,宁可没按钮也不存错 key。
            const clips = tts?.takeCaptured() ?? []
            if (clips.length && convId != null) void audioStore.save(String(convId), new Blob(clips, { type: 'audio/mpeg' }))
            sendBack({ type: 'TURN_DONE' })
        }
    })()
    return () => {
        stopped = true
        task.cancel()
        tts?.abort()
    }
})

function startTurnMessages(messages: Message[], text: string): Message[] {
    return [
        ...messages,
        { id: Date.now(), role: 'user', content: text },
        { id: Date.now() + 1, role: 'assistant', content: '' }
    ]
}

export const chatMachine = setup({
    types: {} as {
        context: Ctx
        events: ChatEvent
        input: { persona: string; onLogout: () => void }
    },
    actors: { asrSession, turnRunner },
    actions: {
        // 半双工:AI 说话期间暂停 VAD;回到聆听时恢复。仅在语音模式(asr actor 存在)时 sendTo。
        pauseAsr: enqueueActions(({ context, enqueue }) => {
            if (context.voiceActive) enqueue.sendTo('asr', { type: 'PAUSE' })
        }),
        resumeAsr: enqueueActions(({ context, enqueue }) => {
            if (context.voiceActive) enqueue.sendTo('asr', { type: 'RESUME' })
        }),
        // 按住说话:把会话收尾从 VAD 切到手动(开始/松开发送/上滑取消)。仅语音模式 sendTo。
        manualStartAsr: enqueueActions(({ context, enqueue }) => {
            if (context.voiceActive) enqueue.sendTo('asr', { type: 'PUSH_START' })
        }),
        manualStopAsr: enqueueActions(({ context, enqueue }) => {
            if (context.voiceActive) enqueue.sendTo('asr', { type: 'PUSH_STOP' })
        }),
        manualCancelAsr: enqueueActions(({ context, enqueue }) => {
            if (context.voiceActive) enqueue.sendTo('asr', { type: 'PUSH_CANCEL' })
        }),
        saveHistory: ({ context }) => saveHistoryCache(context.messages) // 落本地缓存,下次进 Chat 秒显
    }
}).createMachine({
    id: 'chat',
    type: 'parallel',
    context: ({ input }) => ({
        messages: [],
        liveTranscript: '',
        muted: false,
        voiceActive: false,
        asrReady: false,
        capturing: false,
        error: null,
        persona: input.persona,
        tts: null,
        pendingText: '',
        onLogout: input.onLogout
    }),
    on: {
        SET_PERSONA: { actions: assign({ persona: ({ event }) => event.persona }) },
        SET_TTS: { actions: assign({ tts: ({ event }) => event.tts }) },
        INIT_MESSAGES: { actions: assign({ messages: ({ event }) => event.messages }) },
        PREPEND_MESSAGES: {
            actions: assign({ messages: ({ context, event }) => [...event.messages, ...context.messages] })
        },
        // 用真实会话 id 替换本轮 user(末2)+AI(末1)的本地假 id;与历史的 _id*2 / _id*2+1 同构,重载对得上
        SET_TURN_IDS: {
            actions: assign({
                messages: ({ context, event }) => {
                    const next = context.messages.slice()
                    const n = next.length
                    if (n >= 1) next[n - 1] = { ...next[n - 1], id: event.id * 2 + 1 }
                    if (n >= 2) next[n - 2] = { ...next[n - 2], id: event.id * 2 }
                    return next
                }
            })
        },
        ASR_STARTED: { actions: assign({ asrReady: true }) },
        ASR_ERROR: { actions: assign({ error: ({ event }) => event.error }) },
        TURN_ERROR: { actions: assign({ error: ({ event }) => event.error }) },
        MUTE: { actions: [assign({ muted: true }), 'pauseAsr'] },
        UNMUTE: { actions: [assign({ muted: false }), 'resumeAsr'] },
        // 采集信号与 turn 状态无关,放根上;StatusLine 据此把蓝点换成音浪。
        // 会话关时一并清 liveTranscript:任何"无 final 收尾"(取消/静音中断/空会话)都不留残留 partial 气泡。
        ASR_CAPTURE_ON: { actions: assign({ capturing: true }) },
        ASR_CAPTURE_OFF: { actions: assign({ capturing: false, liveTranscript: '' }) }
    },
    states: {
        mode: {
            initial: 'text',
            states: {
                text: { on: { ENTER_VOICE: 'voice' } },
                voice: {
                    entry: assign({ voiceActive: true }),
                    exit: assign({ voiceActive: false, asrReady: false, liveTranscript: '' }),
                    invoke: { id: 'asr', src: 'asrSession', input: { engine: '16k_zh_en' } },
                    on: { ENTER_TEXT: 'text' }
                }
            }
        },
        turn: {
            initial: 'idle',
            states: {
                idle: {
                    // 语音模式 + 麦克风就绪 + 未静音 → 自动进入聆听
                    always: {
                        guard: ({ context }) => context.voiceActive && context.asrReady && !context.muted,
                        target: 'listening'
                    },
                    on: {
                        SEND_TEXT: {
                            actions: assign({
                                messages: ({ context, event }) => startTurnMessages(context.messages, event.text),
                                pendingText: ({ event }) => event.text,
                                error: null
                            }),
                            target: 'active'
                        }
                    }
                },
                listening: {
                    entry: 'resumeAsr',
                    // 不变式:listening 仅在"语音模式 + 未静音"成立。离开语音(切文字)或静音 → 自动回 idle。
                    // (切文字时 ENTER_TEXT 只动 mode 区域,turn 区域得靠这个 always 自己退出,否则文字模式仍显示"聆听中"。)
                    // MUTE 不在此局部处理——否则会遮蔽根上的 MUTE(XState 深层 transition 优先),致 assign(muted)+pauseAsr 不执行。
                    always: { guard: ({ context }) => context.muted || !context.voiceActive, target: 'idle' },
                    on: {
                        // 按住说话:只切换 ASR 收尾方式,不转移 turn(留在 listening);发送仍走下面的 ASR_FINAL
                        MANUAL_START: { actions: 'manualStartAsr' },
                        MANUAL_STOP: { actions: 'manualStopAsr' },
                        // 上滑取消:即时清掉未发送的 partial 气泡(不等 capture-off,避免一帧残留)
                        MANUAL_CANCEL: { actions: [assign({ liveTranscript: '' }), 'manualCancelAsr'] },
                        ASR_PARTIAL: { actions: assign({ liveTranscript: ({ event }) => event.text }) },
                        ASR_FINAL: {
                            guard: ({ event }) => event.text.trim().length > 0,
                            actions: assign({
                                messages: ({ context, event }) =>
                                    startTurnMessages(context.messages, event.text.trim()),
                                pendingText: ({ event }) => event.text.trim(),
                                liveTranscript: '',
                                error: null
                            }),
                            target: 'active'
                        },
                        SEND_TEXT: {
                            actions: assign({
                                messages: ({ context, event }) => startTurnMessages(context.messages, event.text),
                                pendingText: ({ event }) => event.text,
                                error: null
                            }),
                            target: 'active'
                        }
                    }
                },
                active: {
                    entry: 'pauseAsr',
                    invoke: {
                        id: 'turn',
                        src: 'turnRunner',
                        input: ({ context }) => ({
                            text: context.pendingText,
                            persona: context.persona,
                            tts: context.tts,
                            onLogout: context.onLogout
                        })
                    },
                    initial: 'thinking',
                    states: {
                        thinking: { on: { FIRST_TOKEN: 'speaking' } },
                        speaking: {}
                    },
                    on: {
                        REPLY_DELTA: {
                            actions: assign({
                                messages: ({ context, event }) => {
                                    const next = context.messages.slice()
                                    if (next.length)
                                        next[next.length - 1] = { ...next[next.length - 1], content: event.content }
                                    return next
                                }
                            })
                        },
                        TURN_DONE: { actions: 'saveHistory', target: 'idle' },
                        INTERRUPT: { target: 'idle' }
                    }
                }
            }
        }
    }
})

// ── 选择器:状态 → 视图的单一来源 ──
// 机器只管"是什么状态";"怎么显示"全部从这里派生,避免散在组件里写三元链(那是 muted 泄漏那类 bug 的温床)。
type ChatSnapshot = SnapshotFrom<typeof chatMachine>

export function selectMode(s: ChatSnapshot): Mode {
    return s.matches({ mode: 'voice' }) ? 'voice' : 'text'
}

export function selectCapturing(s: ChatSnapshot): boolean {
    return s.context.capturing
}

export function selectStatus(s: ChatSnapshot): Status {
    if (s.matches({ turn: { active: 'thinking' } })) return 'thinking'
    if (s.matches({ turn: { active: 'speaking' } })) return 'speaking'
    if (s.matches({ turn: 'listening' })) return 'listening'
    return 'idle'
}

export function selectStatusText(s: ChatSnapshot): string {
    const status = selectStatus(s)
    // muted 是语音概念(暂停 VAD);文字模式不受它影响,照常显示 AI 思考/说话
    if (selectMode(s) === 'voice' && s.context.muted) return '已静音'
    if (status === 'thinking') return 'AI思考中…'
    if (status === 'speaking') return 'AI说话中…'
    if (status === 'listening') return '聆听中，沉默 1 秒后结束'
    return ''
}

